<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-subtitle="msgSelectedRows"
      :section-icon="'far fa-user-md'"
      :section-title="$tc('configuration.sections.petitioners', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deletePetitioners(selectedRows)"
        @shortkey="deletePetitioners(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showPetitionerModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showPetitionerModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showPetitionerModal($t('configuration.actions.add'))"
        @shortkey="showPetitionerModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        :autosize-columns="false"
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="petitionerModal">
      <petitioner-modal
        :action-title="actionTitle"
        @update="patchPetitioners($event)"
        @create="createPetitioner($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsPetitionersGrid from '@/modules/configurationModule/detail/peopleManagement/petitioners/columnsPetitionersGrid'
import PetitionerModal from '@/modules/configurationModule/detail/peopleManagement/petitioners/PetitionerModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import PetitionerConfigurationServices from '@/api/resources/configuration/peopleManagement/petitioner/PetitionerConfigurationServices'

export default {
    name: 'PeoplePetitionersGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        PetitionerModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.peopleManagement.petitioners',
            actionTitle: null,
            selectedRows: [],
            mode: {
                size: 25
            },
            columnDefs: columnsPetitionersGrid()
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        hasActivePetitioner() {
            return !!this.selectedRows.find(petitioner => petitioner.active)
        },
        hasDeactivePetitioner() {
            return !!this.selectedRows.find(petitioner => !petitioner.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(petitioner => petitioner.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(petitioner => !petitioner.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActivePetitioner && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivePetitioner && !this.hasAllActivated
        }
    },
    async mounted() {
        if (this.$route.params.petitionerId) {
            const [err, response] = await this.toCall(PetitionerConfigurationServices.getPetitionerById(this.$route.params.petitionerId))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
            }
            this.showPetitionerModal(this.$t('configuration.actions.update'), response)
        }
    },
    methods: {
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(PetitionerConfigurationServices))
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        },
        async patchPetitioners(...petitioners) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(PetitionerConfigurationServices.patchPetitionerBatch(petitioners))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.petitionerModal.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createPetitioner(petitioner) {
            const response = await this.$_toCallUnique(PetitionerConfigurationServices.createPetitioner(petitioner))
            if (response) {
                this.$refs.petitionerModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deletePetitionersBatch(petitioners) {
            const petitionersToDelete = petitioners.map(petitioner => petitioner.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(PetitionerConfigurationServices.deletePetitionersBatch(petitionersToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (petitionersToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: petitionersToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        async activateDeactivateSelection(option, selectionBatch) {
            const petitionersToPatch = selectionBatch.filter(petitioner => petitioner.active !== option).map(petitioner => {
                return {
                    active: option,
                    id: petitioner.id
                }
            })
            if (petitionersToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(PetitionerConfigurationServices.patchPetitionerBatch(petitionersToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showPetitionerModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.selectedRows.length ? this.$t('configuration.actions.deleteSelection') : this.$t('configuration.actions.delete'),
                        action: () => {
                            const petitionersToDelete = this.selectedRows.length ? this.selectedRows : [params?.node.data]
                            this.deletePetitioners(petitionersToDelete)
                        }
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    },
                    'separator',
                    {
                        name: params?.node.data.user ? this.$t('configuration.actions.updateUser') : this.$t('configuration.modal.goToUser'),
                        action: () => {
                            const goToRoute = { name: 'PeopleUserapp', params: { actions: this.getActions(params?.node.data) } }
                            this.$router.push(goToRoute)
                        }
                    }
                ]
            }
        },
        getActions(entity) {
            let actionObj = {}
            if (entity.user) {
                actionObj = { updateUser: { userId: entity.user.id } }
            } else {
                actionObj = { createUser: { petitionerId: entity.id } }
            }
            return actionObj
        },
        setColumns(columns) {
            let columnDefs = columns
            if (!this.globalParameters.middleNameSupported) {
                columnDefs = columns.filter(col => col.field !== 'middleName')
            }
            return columnDefs
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(this.setColumns(this.columnDefs))
            api.setServerSideDatasource(new PaginatorDatasource(PetitionerConfigurationServices))
        },
        showPetitionerModal(title, selectedPetitioner) {
            this.actionTitle = title
            this.$refs.petitionerModal.show([selectedPetitioner])
        },
        async deletePetitioners(petitioners) {
            if (petitioners.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${petitioners.length} ${this.$tc('configuration.grid.element', petitioners.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deletePetitionersBatch(petitioners)
                }
            }
        }
    }
}
</script>
